import { cn } from "@/lib/utils";
import { SpinnerGapIcon } from "@phosphor-icons/react";
import { type VariantProps, cva } from "class-variance-authority";
import type { FC } from "react";

const rankedListItemVariants = cva("flex items-center justify-between py-0.5", {
  variants: {
    size: {
      sm: "text-xxs",
      md: "text-xs",
      lg: "text-sm",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

type RankedListItemProps = {
  rank: number;
  color: string; // Tailwind class or hex code like "#50d71e"
  label: string;
  count: number;
  percentage: number;
  className?: string;
  isLoading?: boolean;
} & VariantProps<typeof rankedListItemVariants>;

/**
 * `RankedListItem` is a component that displays a ranked list item with a rank,
 * color, label, count, and percentage. It is used to display a ranked list of
 * items in a list.
 */
export const RankedListItem: FC<RankedListItemProps> = ({
  rank,
  color,
  label,
  count,
  percentage,
  size = "sm",
  className,
  isLoading = false,
}) => {
  const isTopRank = rank === 1;

  // Check if it's a hex color like "#50d71e" or "#fff"
  const isHex = /^#([0-9A-F]{3}){1,2}$/i.test(color);

  return (
    <div
      className={cn(
        rankedListItemVariants({ size }),
        "w-full min-w-0",
        className,
      )}
    >
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <div
          className={cn(
            "text-[9px] font-medium w-7 h-5 flex items-center justify-center",
            isTopRank
              ? "bg-gray-100 text-gray-800 rounded-full"
              : "text-gray-600",
          )}
        >
          #{rank}
        </div>

        <span
          className={cn("w-2 h-2 rounded-full shrink-0", !isHex && color)}
          style={isHex ? { backgroundColor: color } : undefined}
        />

        <span
          className={cn(
            "text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap block min-w-0 flex-1",
            size === "sm" ? "text-xxs" : size === "md" ? "text-xs" : "text-sm",
          )}
        >
          {label}
        </span>
      </div>

      <div
        className={cn(
          rankedListItemVariants({ size }),
          "text-gray-600 tracking-[0.11px] shrink-0",
        )}
      >
        {isLoading ? (
          <SpinnerGapIcon className={"animate-spin"} size={12} />
        ) : (
          `${count} (${percentage}%)`
        )}
      </div>
    </div>
  );
};
